import { useApp } from '../store';
import type { View } from '../store';
import { visibleComments } from '../comments';
import { displayName } from '../people';
import VendorTag from './VendorTag';
import { mentions, splitMentions, namePeople } from '../mentions';
import { markSeen } from '../seen';
import { historyTime } from '../utils';
import { useEffect, useMemo, useState } from 'react';
import { A_PAGE, at } from '../paging';
import { PageSize, Pages } from './Pages';
import { periodsFor, within } from '../activity-when';
import { DOCUMENT_KIND } from '../documents';
import type { Doc, Signoff, Build } from '../types';
import type { Sprint } from '../sprints';

/* The first page and the sizes on offer are in src/paging.ts now, shared with
   the other two lists — #310, "pages on both, keep it consistent". */

type Entry = {
  key: string;
  who: string;
  at: number;
  what: string;
  kind: 'comment' | 'change' | 'signoff' | 'update';
  /** An issue row opens the card; set on comment/change/update entries. */
  issueId?: string;
  ref?: string;
  title?: string;
  /** When the words were last changed, on a comment or a status update. Shows a
      quiet "· edited" mark so this screen agrees with the card, which has always
      shown one. */
  editedAt?: number;
  /** A sign-off row drills to where its subject is answered — #signoff-r1.
      A document/release/sprint is not an issue, so it routes rather than
      opening a card. */
  nav?: { view: View; reveal?: string };
};

/**
 * WHERE A SIGN-OFF ROW POINTS, and what to call the thing it is against.
 *
 * A sign-off is not an issue, so it cannot open a card — it drills to the screen
 * its subject is answered on (a document in Documents, a finished sprint in
 * Sprints, a release in Releases) and reveals it. The label is written so the
 * sentence beside a name reads on its own: "approved the change request …".
 */
function signoffSubject(
  s: Signoff,
  documents: Doc[],
  sprints: Sprint[],
  builds: Build[]
): { label: string; nav: { view: View; reveal?: string } } | null {
  if (s.documentId) {
    const d = documents.find((x) => x.id === s.documentId);
    if (!d) return null;
    return {
      label: `the ${DOCUMENT_KIND[d.kind].toLowerCase()} “${d.title}”`,
      nav: { view: 'documents', reveal: d.id },
    };
  }
  if (s.sprintId) {
    const sp = sprints.find((x) => x.id === s.sprintId);
    return {
      label: sp ? `the “${sp.name}” sign-off` : 'a sprint sign-off',
      nav: { view: 'sprints', reveal: s.sprintId },
    };
  }
  if (typeof s.build === 'number') {
    const b = builds.find((x) => x.build === s.build);
    return { label: b ? `release ${b.version}` : 'a release', nav: { view: 'releases', reveal: b?.id } };
  }
  return null;
}

/**
 * EVERY SIGN-OFF ANSWER AS A FEED ENTRY — #signoff-r1.
 *
 * The board Activity showed only issue changes and comments, so a document sent
 * for sign-off, every approval and rejection on it, and its being signed off or
 * turned down left no trace on the board at all — the one screen that promises
 * "every change across the board". Derived from state the same way the issue
 * feed is (no new rows written): one entry per answer actually given, carrying
 * who answered, when, and — on a refusal — why.
 *
 * The answers are what is surfaced rather than the raise, because they are what
 * the record can attribute: an answer names the person, the moment and (for a
 * rejection) the reason, where "sent for sign-off" is stamped with no actor. The
 * approvals and refusals are the substance of the trail regardless.
 */
function signoffFeed(
  documents: Doc[],
  signoffs: Signoff[],
  sprints: Sprint[],
  builds: Build[]
): Entry[] {
  const out: Entry[] = [];
  for (const s of signoffs) {
    /* An answer that was actually given and not withdrawn. `answer` absent
       means approved (every row written before rejections could be recorded). */
    if (!s.signedAt || s.withdrawnAt) continue;
    const subj = signoffSubject(s, documents, sprints, builds);
    if (!subj) continue;
    const rejected = s.answer === 'rejected';
    const who = s.onBehalfBy || s.signatoryEmail || s.name || '';
    let what = `${rejected ? 'rejected' : 'approved'} ${subj.label}`;
    if (s.onBehalfBy) what += ` on behalf of ${s.name}`;
    else if (s.answeredVia === 'link') what += ' through their signing link';
    if (rejected && s.why) what += ` — “${s.why}”`;
    out.push({ key: `so-${s.id}`, who, at: s.signedAt, what, kind: 'signoff', nav: subj.nav });
  }
  return out;
}

/*
 * The exact sentences record_issue_change writes for a change to the `stands`
 * scalar (migration 0141). They carry no words, so the feed shows the status-log
 * entry itself instead — see the note where these are skipped. Kept in one place
 * so a change to the server wording is a change in one place here too.
 */
const STATUS_LOG_MARKERS = new Set(['posted an update', 'edited an update', 'removed an update']);

/** Everything that has happened across the board, newest first. */
export function buildFeed(
  issues: ReturnType<typeof useApp.getState>['issues'],
  documents: Doc[] = [],
  signoffs: Signoff[] = [],
  sprints: Sprint[] = [],
  builds: Build[] = []
): Entry[] {
  const out: Entry[] = [];
  for (const i of issues) {
    for (const c of visibleComments(i.comments)) {
      out.push({
        key: `c${c.id}`,
        issueId: i.id,
        ref: i.ref,
        title: i.title,
        who: c.author,
        at: c.createdAt,
        what: c.text,
        kind: 'comment',
        editedAt: c.editedAt,
      });
    }
    for (const a of i.activity ?? []) {
      /*
       * THE STATUS LOG WRITES A TEXT-LESS MARKER HERE, and the words go below.
       *
       * The server records a `stands` scalar change as "posted an update",
       * "edited an update" or "removed an update" — no words (migration 0141).
       * Those markers are replaced by the update rows built below, which carry
       * the actual text; showing both would print every update twice, once
       * without its words. A quiet NOTE moves `stands` too, so its marker would
       * leak onto the board as "posted an update" even though posting it told no
       * one — dropping the marker keeps the Note off here the same way. Every
       * other change (a move, a re-sizing, a date, a sprint) stays.
       */
      if (STATUS_LOG_MARKERS.has(a.what)) continue;
      out.push({
        key: `a${a.id}`,
        issueId: i.id,
        ref: i.ref,
        title: i.title,
        who: a.who,
        at: a.at,
        what: a.what,
        kind: 'change',
      });
    }
    /*
     * STATUS UPDATES — the narration of where the work stands — live in
     * standsLog, which this feed never read, so a posted update showed on the
     * card and nowhere on the board that promises "every comment and change".
     * Surface them at the words they currently carry (an edit shows a "· edited"
     * mark, like the card). A quiet NOTE tells no one, so it stays off the board
     * the same way posting it raised no call-out; an `auto` line is a bare move
     * the activity rows above already carry, and carries no words of its own.
     */
    for (const u of i.standsLog ?? []) {
      if (u.auto || u.quiet || !u.text.trim()) continue;
      out.push({
        key: `u${u.id}`,
        issueId: i.id,
        ref: i.ref,
        title: i.title,
        who: u.by,
        at: u.at,
        what: u.text,
        kind: 'update',
        editedAt: u.editedAt,
      });
    }
  }
  for (const e of signoffFeed(documents, signoffs, sprints, builds)) out.push(e);
  return out.sort((x, y) => y.at - x.at);
}

export default function Activity() {
  const issues = useApp((s) => s.issues);
  const documents = useApp((s) => s.documents);
  const signoffs = useApp((s) => s.signoffs);
  const sprints = useApp((s) => s.sprints);
  const builds = useApp((s) => s.builds);
  const openIssue = useApp((s) => s.openIssue);
  const drillTo = useApp((s) => s.drillTo);
  const lastSeen = useApp((s) => s.lastSeen);
  const profiles = useApp((s) => s.profiles);
  const setLastSeen = useApp((s) => s.setLastSeen);
  const myEmail = useApp((s) => s.user?.email ?? '');

  const all = useMemo(
    () => buildFeed(issues, documents, signoffs, sprints, builds),
    [issues, documents, signoffs, sprints, builds]
  );

  /* Open whatever the row is about: a card opens in place; a sign-off drills to
     the screen it is answered on and reveals it. */
  const open = (e: Entry) => {
    if (e.kind === 'signoff' && e.nav) drillTo(e.nav.view, e.nav.reveal);
    else if (e.issueId) openIssue(e.issueId);
  };

  /*
   * ── REACHING WHAT THE COUNT USED TO WITHHOLD — #296 ───────────────────────
   *
   * `now` is taken once per mount rather than on every render: the periods are
   * derived from it, and a "Today" whose boundary moves underneath a rendered
   * list would quietly change what is on screen while somebody reads it.
   */
  const [now] = useState(() => Date.now());
  const [periodId, setPeriodId] = useState('all');
  const periods = useMemo(() => periodsFor(all.map((e) => e.at), now), [all, now]);
  const period = periods.find((p) => p.id === periodId) ?? periods[0];
  const inPeriod = useMemo(() => all.filter((e) => within(e.at, period)), [all, period]);

  /*
   * ── AND THE CAP NO LONGER DEAD-ENDS INSIDE A MONTH EITHER ─────────────────
   *
   * ⚠️ THE FIRST VERSION OF THIS REPRODUCED THE FAULT IT WAS FIXING, one level
   * down. The date filter was added because "Showing the latest 200 of 986" had
   * nowhere to go — and then a month holding more than 200 entries got the same
   * cap, with a sentence ending in a full stop because the period was bounded
   * and therefore "always shown whole". A witness built a June with 252 entries
   * on 26 August 2026: fifty-one of them could not be reached from any choice in
   * the picker. The comment claiming a month is always whole was true only of
   * months under two hundred.
   *
   * The affordance is the same one the audit trail uses — draw a first page,
   * say how many there are, and offer the rest — so the two screens behave
   * alike rather than each having its own idea of what a cap means.
   *
   * IT RESETS WITH THE PERIOD. Choosing a different month while showing six
   * hundred rows would hand the next month six hundred too, which is a slow
   * screen nobody asked for.
   */
  /*
   * ── AND PAGES REPLACED SHOW MORE — #310 ─────────────────────────────────
   *
   * Vinnie, 30 August 2026: *"should have pages as it is a lot to scroll even
   * at 200. The filter should remain."* The stretch picker above is untouched;
   * this is beside it, never instead of it.
   *
   * IT RESETS WITH THE PERIOD, for the reason Show more did: being on page 6
   * and then choosing a month with two pages in it draws nothing at all, which
   * looks exactly like a month where nothing happened. `at` clamps as well —
   * belt and braces, because that empty screen is indistinguishable from the
   * real thing and nobody would think to press Back.
   */
  const [size, setSize] = useState(A_PAGE);
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [periodId, size]);
  const where = at(inPeriod.length, size, page);
  const feed = useMemo(() => inPeriod.slice(where.start, where.end), [inPeriod, where.start, where.end]);

  // Must match the badge exactly. It counted only other people's activity while
  // the rows marked your own as "New" too, so your own comment showed up flagged
  // against a badge reading zero.
  // Only somebody else's action, unseen, is "news" — and only when it has an
  // actor to attribute (`e.who`). Must match the badge in App.tsx exactly.
  const isNew = (e: Entry) => !!e.who && e.at > lastSeen && e.who !== myEmail;

  // Opening this screen is what "reading" means — stamp it on the way out so the
  // badge clears, but leave the "new" markers visible for this visit. Closing the
  // tab counts too: stamping only on unmount meant sitting here and closing the
  // browser left everything unread for ever.
  useEffect(() => {
    const stamp = () => {
      const now = Date.now();
      markSeen(now);
      setLastSeen(now);
    };
    window.addEventListener('pagehide', stamp);
    return () => {
      window.removeEventListener('pagehide', stamp);
      stamp();
    };
  }, [setLastSeen]);

  /*
   * TWO DIFFERENT EMPTIES, and they were one before this. "Nothing has happened
   * yet" is true of a new board and false of a quiet fortnight — and told to
   * somebody who has just chosen a month, it reads as though the board itself
   * were empty rather than that month.
   */
  if (all.length === 0) {
    return (
      <div className="bl-empty">
        Nothing has happened yet. Comments and changes will show up here.
      </div>
    );
  }

  return (
    <div className="backlog">
      <div className="act-when">
        <label className="act-when-pick">
          Showing
          <select value={period.id} onChange={(e) => setPeriodId(e.target.value)}>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <p className="hint releases-intro">
          Every comment and change across the board, newest first.
          {/*
            THE COUNT NOW COMES WITH A WAY OUT. It used to say "Showing the
            latest 200 of 986" on a screen with no next page and no filter —
            the number you were missing, and nothing to do about it.

            A MONTH IS ALWAYS SHOWN WHOLE, because it is bounded at both ends,
            so the sentence can point at one instead of apologising. Without
            the months, "older than thirty days" would be reachable only
            through All — which is the capped view, and the same dead end in a
            new coat.
          */}
          {/*
            ✏️ AND IT NO LONGER POINTS THE WRONG WAY. This said "choose a month
            BELOW" while the picker sits above the list and to the left of this
            very sentence — and the empty message on the same screen says
            "above" about the identical control. A witness measured both boxes
            on 26 August 2026. With the button below doing the work, the
            sentence no longer has to send anybody anywhere.
          */}
          {/* The count stays — #294's fault was the SILENT cut, and that stays
              fixed whichever way the reader moves. It now sits beside the way
              on rather than in front of a dead end. */}
          {inPeriod.length > feed.length &&
            ` Showing ${where.from}–${where.to} of ${inPeriod.length}.`}
        </p>
        <PageSize size={size} onSize={setSize} />
      </div>
      {/* A CHOSEN STRETCH WITH NOTHING IN IT IS NOT AN EMPTY BOARD, and saying
          so names the way out. The month choices are built from the entries, so
          a month that is offered always has something in it — but Today and the
          last seven days are offered whether or not anything happened. */}
      {feed.length === 0 ? (
        <div className="bl-empty">
          Nothing in {period.label.toLowerCase()}. Choose a longer stretch above.
        </div>
      ) : (
      <div className="bl-rows">
        {feed.map((e) => (
          <article
            key={e.key}
            className={`act-row ${isNew(e) ? 'unread' : ''} ${mentions(e.what, myEmail) ? 'calls-me' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => open(e)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                open(e);
              }
            }}
          >
            <div className="act-main">
              {e.kind === 'signoff' ? (
                /* A SIGN-OFF READS AS ONE SENTENCE — "Marcia Alleyne approved the
                   change request …", or, for a subject sent for sign-off (which
                   carries no person), the sentence on its own. No issue ref, and
                   the reason is already in the sentence, so there is no second
                   line. */
                <div className="act-head">
                  {isNew(e) && <span className="act-new">New</span>}
                  {e.who && <span className="act-who">{displayName(e.who, profiles)}</span>}
                  {e.who && <VendorTag who={e.who} profiles={profiles} compact />}
                  <span className="act-what act-what-inline">{e.what}</span>
                </div>
              ) : (
                <>
                  <div className="act-head">
                    {isNew(e) && <span className="act-new">New</span>}
                    {/* SOMEBODY ASKING YOU SOMETHING IS NOT THE SAME AS SOMETHING
                        HAPPENING. This screen marked both as "New" and nothing else,
                        so a question addressed to you sat in the same grey list as
                        forty status changes and was missed. */}
                    {mentions(e.what, myEmail) && <span className="act-called">Mentioned you</span>}
                    {/* Everywhere else on the board shows a person's name. This one
                        screen printed the raw address, which is how you learn that
                        "People with names and profiles" never reached it. */}
                    <span className="act-who">{displayName(e.who, profiles)}</span>
                    <VendorTag who={e.who} profiles={profiles} compact />
                    <span className="act-verb">
                      {e.kind === 'comment'
                        ? 'commented on'
                        : e.kind === 'update'
                          ? 'posted an update on'
                          : 'updated'}
                    </span>
                    <span className="bl-ref">{e.ref}</span>
                    <span className="act-title">{e.title}</span>
                    {/* The same quiet mark the card carries, so a reworded comment
                        or update reads the same on both screens. */}
                    {e.editedAt && (
                      <span className="feed-edited" title={`Edited ${historyTime(e.editedAt)}`}>
                        · edited
                      </span>
                    )}
                  </div>
                  {/* A MENTION IS STORED AS AN ADDRESS AND SHOWN AS A NAME.
                      The card's own comment list has always done this; here it did
                      not, so the same comment read "@pat@example.com" on one screen
                      and "@Pat Moore" on the other. Nobody writes an address when
                      they mean a person. */}
                  <p className="act-what">
                    {/* A CHANGE THE SERVER RECORDED CAN HOLD A RAW ADDRESS — "set
                        assignee to pat@example.com" — so those are named too, not
                        only the @mentions. A comment is left as its author typed
                        it. */}
                    {splitMentions(
                      e.kind === 'change' ? namePeople(e.what, profiles) : e.what,
                      profiles
                    ).map((part, n) =>
                      part.mention ? (
                        <span
                          key={n}
                          className={`mention ${part.mention === myEmail.toLowerCase() ? 'is-me' : ''}`}
                          title={part.mention}
                        >
                          {part.text}
                        </span>
                      ) : (
                        <span key={n}>{part.text}</span>
                      )
                    )}
                  </p>
                </>
              )}
            </div>
            {/* This screen IS the history, so it reads the same way the one
                inside a card does — a clock and a day, not an elapsed count. */}
            <span className="bl-time">{historyTime(e.at)}</span>
          </article>
        ))}
      </div>
      )}
      <Pages
        total={inPeriod.length}
        size={size}
        page={where.page}
        onPage={setPage}
        what="changes"
      />
    </div>
  );
}
